import type { InjectionKey } from 'vue'
import { injectStrict } from './utils/inject'
import { createDirectHttpClient, DirectHttpError } from '../../shared/http/direct-client'
import type { DirectHttpClient } from '../../shared/http/direct-client'
import type {
  Availability,
  Booking,
  Brand,
  Journey,
  PayloadRequestItem,
  VehicleModel,
  TicketCreatePayload,
  TicketAvailability,
  TicketContact,
  TicketPassenger,
  TicketPricingTariff,
} from '../../shared/types'

// Contrat de création de ticket : SSOT dans `shared/types` (partagé widget ↔
// SaaS). Ré-exporté ici pour garder les call sites widget (`from './api'`)
// stables.
export type {
  TicketCreatePayload,
  TicketAvailability,
  TicketContact,
  TicketPassenger,
  TicketPricingTariff,
}

export interface ApiConfig {
  baseUrl: string
  apiKey: string
  orgId?: string
  timeoutMs?: number
  /**
   * Langue du widget (attribut `lang`), envoyée en `Accept-Language` sur chaque appel :
   * les libellés renvoyés (places, options) suivent la langue affichée, pas celle du
   * navigateur.
   */
  language?: string
}

export interface AvailabilitySearchResult {
  outward: Availability[]
  return: Availability[]
}

export class ApiError extends Error {
  constructor(
    readonly httpStatus: number,
    message: string,
    readonly backendCode?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method: Method
  path: string
  body?: unknown
  headers?: Record<string, string>
}

export class WidgetApi {
  /**
   * Transport bas-niveau partagé avec le SaaS (`#ferry/shared/http`). Gère
   * URL/headers Bearer/timeout/parse/unwrap. La classe ne garde que
   * l'orchestration métier (multi-provider `allSettled`, guards orgId, mapping
   * vers `ApiError`).
   */
  private readonly http: DirectHttpClient

  constructor(private readonly cfg: ApiConfig) {
    this.http = createDirectHttpClient({
      baseUrl: cfg.baseUrl,
      apiKey: cfg.apiKey,
      timeoutMs: cfg.timeoutMs,
    })
  }

  get orgId(): string | undefined {
    return this.cfg.orgId
  }

  listJourneys(language?: string): Promise<Journey[]> {
    return this.request<Journey[]>({
      method: 'GET',
      path: '/api/journey',
      headers: language ? { 'Accept-Language': language } : undefined,
    })
  }

  /**
   * Fetch full journey details (origin/destination, age ranges, and most
   * importantly the full `departure[]` list). The backend returns BOTH the
   * Outward and Return versions of the same route when `direction=Return` is
   * passed, so the response is an array — typically of length 2.
   */
  getJourneyVariants(journeyId: string): Promise<Journey[]> {
    return this.request<Journey[]>({
      method: 'GET',
      path: `/api/journey/${encodeURIComponent(journeyId)}?direction=Return`,
    })
  }

  listVehicleBrands(): Promise<Brand[]> {
    return this.request<Brand[]>({ method: 'GET', path: '/api/vehicle-brand' })
  }

  listVehicleModels(brandId: string): Promise<VehicleModel[]> {
    return this.request<VehicleModel[]>({
      method: 'GET',
      path: `/api/vehicle-data?brand=${encodeURIComponent(brandId)}`,
    })
  }

  /**
   * Backend expects ONE payload object per call — `{ request, passengers,
   * vehicle, pets }` — not an array. When the search spans multiple
   * carriers/routes, we fire N parallel calls and merge the responses.
   *
   * Response shapes the backend may return (we normalize them all):
   *   - `Availability[]` (flat array, items have `direction: 'Outward'|'Return'`)
   *   - `{ availabilities: Availability[] }`
   *   - `{ outward: Availability[], return: Availability[] }`
   *
   * Tolérance aux pannes par provider : chaque compagnie est interrogée
   * indépendamment, donc une recherche multi-carriers ne doit PAS échouer en
   * bloc si un seul provider renvoie une erreur (ex. Balearia 503 « No hay
   * trayectos »). On utilise `allSettled` : on agrège les réponses valides et
   * on ne propage une erreur que si TOUS les providers échouent.
   */
  async searchAvailability(items: PayloadRequestItem[]): Promise<AvailabilitySearchResult> {
    if (items.length === 0) return { outward: [], return: [] }
    const settled = await Promise.allSettled(
      items.map((item) =>
        this.request<unknown>({
          method: 'POST',
          path: '/api/availability',
          body: item,
        }),
      ),
    )
    const outward: Availability[] = []
    const ret: Availability[] = []
    let firstError: unknown = null
    let okCount = 0
    for (const r of settled) {
      if (r.status === 'fulfilled') {
        okCount++
        splitByDirection(r.value, outward, ret)
      } else if (firstError === null) {
        firstError = r.reason
      }
    }
    // Aucun provider n'a répondu → on remonte l'erreur (sinon écran vide muet).
    // Au moins une réponse OK → on affiche les résultats valides, panne(s)
    // provider silencieuse(s).
    if (okCount === 0 && firstError !== null) throw firstError
    return { outward, return: ret }
  }

  /**
   * Create a ticket (booking option) on the org scope. The backend endpoint
   * is `POST /organisation/{orgId}/ticket` and expects the full structured
   * payload (availability[], passengers[], params.pricing). The wgt_ token
   * must be scoped to this organisation.
   */
  createTicket(payload: TicketCreatePayload): Promise<Booking> {
    if (!this.cfg.orgId) {
      throw new ApiError(400, 'Missing orgId — cannot create ticket without organisation scope')
    }
    return this.request<Booking>({
      method: 'POST',
      path: `/organisation/${encodeURIComponent(this.cfg.orgId)}/ticket`,
      body: payload,
    })
  }

  payBooking(ticketId: string): Promise<{ success: boolean; ticketId: string }> {
    return this.request({
      method: 'POST',
      path: `/settlement/booking/${encodeURIComponent(ticketId)}/pay`,
      body: {},
    })
  }

  async getVoucherUrl(ticketId: string): Promise<string | null> {
    const data = await this.request<{ file?: string | null }>({
      method: 'GET',
      path: `/api/ticket/${encodeURIComponent(ticketId)}/pdf`,
    })
    return data.file ?? null
  }

  private async request<T>(opts: RequestOptions): Promise<T> {
    try {
      return await this.http.request<T>({
        method: opts.method,
        path: opts.path,
        body: opts.body,
        headers: this.cfg.language
          ? { 'Accept-Language': this.cfg.language, ...opts.headers }
          : opts.headers,
      })
    } catch (err: unknown) {
      // Le client partagé throw `DirectHttpError` (HTTP >= 400 ou réseau). On
      // remappe vers l'`ApiError` publique du widget (consommée par les
      // composants + le guard orgId de `createTicket`). On préserve le fallback
      // « detail comme message » de l'ancien `errorMessage`.
      if (err instanceof DirectHttpError) {
        const detail = extractDetail(err.body)
        throw new ApiError(
          err.httpStatus,
          err.message || detail || `Upstream ${err.httpStatus}`,
          err.code,
        )
      }
      throw err
    }
  }
}

/** Fallback `detail` (top-level ou imbriqué) utilisé comme message si besoin. */
function extractDetail(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const b = body as Record<string, unknown>
  if (typeof b.detail === 'string') return b.detail
  return undefined
}

export const API_KEY: InjectionKey<WidgetApi> = Symbol('fg-widget-api')

export function useApi(): WidgetApi {
  return injectStrict(API_KEY, 'WidgetApi')
}

function splitByDirection(response: unknown, outward: Availability[], ret: Availability[]): void {
  const list = toAvailabilityList(response)
  if (list) {
    for (const item of list) (item.direction === 'Return' ? ret : outward).push(item)
    return
  }
  if (response && typeof response === 'object') {
    const wrapper = response as { outward?: Availability[]; return?: Availability[] }
    if (Array.isArray(wrapper.outward)) outward.push(...wrapper.outward)
    if (Array.isArray(wrapper.return)) ret.push(...wrapper.return)
  }
}

function toAvailabilityList(response: unknown): Availability[] | null {
  if (Array.isArray(response)) return response as Availability[]
  if (response && typeof response === 'object') {
    const obj = response as { availabilities?: unknown; data?: { availabilities?: unknown } }
    if (Array.isArray(obj.availabilities)) return obj.availabilities as Availability[]
    if (obj.data && typeof obj.data === 'object' && Array.isArray(obj.data.availabilities)) {
      return obj.data.availabilities as Availability[]
    }
  }
  return null
}

